import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFixedPhoneSlice } from "app/pages/Customer/FixedPhone/slice";
import type { SimAvailabilityData } from "app/pages/Customer/Activation/slice/types";
import { applyUpsellPlansToActivationStorage } from "../../FixedPhoneFlow/utils/activation-plan-helpers";
import { getActivationCodeIncludedPlanResolution } from "./activation-code-flow";

interface Params {
  data?: SimAvailabilityData;
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * Continue / upsell navigation after activation-code device verification congratulations.
 */
export function useActivationCodeCongratulationsContinue({
  data,
  showDialog,
  setShowDialog,
}: Params) {
  const navigate = useNavigate();
  const { useGetFixedPhoneUpsellPlansQuery } = useFixedPhoneSlice();

  const includedPlanResolution = useMemo(
    () => getActivationCodeIncludedPlanResolution(data),
    [data]
  );

  const [shouldFetchUpsellPlans, setShouldFetchUpsellPlans] = useState(false);
  const {
    data: upsellPlansData,
    isFetching: isUpsellFetching,
    isError: isUpsellError,
  } = useGetFixedPhoneUpsellPlansQuery({}, { skip: !shouldFetchUpsellPlans });
  const upsellPlans = useMemo(
    () => upsellPlansData?.data || [],
    [upsellPlansData]
  );

  const upsellNavigationCommittedRef = useRef(false);

  useEffect(() => {
    if (!shouldFetchUpsellPlans) {
      upsellNavigationCommittedRef.current = false;
      return;
    }
    if (isUpsellFetching) return;
    if (isUpsellError) {
      if (upsellNavigationCommittedRef.current) return;
      upsellNavigationCommittedRef.current = true;
      navigate("/activation/fixed-phone/choose-service-plan");
      return;
    }
    if (upsellPlansData !== undefined) {
      if (upsellNavigationCommittedRef.current) return;
      upsellNavigationCommittedRef.current = true;
      if (upsellPlans.length > 0) {
        applyUpsellPlansToActivationStorage(upsellPlans, "yearly");
        navigate("/activation/fixed-phone/security-plan-free-trial");
      } else {
        navigate("/activation/fixed-phone/choose-service-plan");
      }
    }
  }, [
    shouldFetchUpsellPlans,
    isUpsellFetching,
    isUpsellError,
    upsellPlans,
    upsellPlansData,
    navigate,
  ]);

  const continueTapLockRef = useRef(false);

  useEffect(() => {
    if (showDialog) {
      continueTapLockRef.current = false;
    }
  }, [showDialog]);

  const handleContinue = useCallback(() => {
    if (continueTapLockRef.current) return;
    continueTapLockRef.current = true;
    try {
      setShowDialog(false);
      if (includedPlanResolution === "yearly") {
        setShouldFetchUpsellPlans(true);
        return;
      }
      navigate("/activation/fixed-phone/choose-service-plan");
    } catch {
      continueTapLockRef.current = false;
    }
  }, [includedPlanResolution, navigate, setShowDialog]);

  const continueDisabled =
    includedPlanResolution === "yearly" &&
    shouldFetchUpsellPlans &&
    isUpsellFetching;

  return { handleContinue, continueDisabled };
}
